import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Award,
  Backpack,
  BarChart3,
  CalendarDays,
  Check,
  Dumbbell,
  MapPin,
  ShieldCheck,
  Sparkles,
  Target,
  Trophy,
  Users,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { trackEvent, attributionForSubmit } from "@/lib/analytics";

const pillars = [
  {
    icon: Users,
    title: "Team Training",
    copy: "Two coached team sessions each week focused on mechanics, movement, strategy, and competitive decision-making.",
  },
  {
    icon: Target,
    title: "Daily Development",
    copy: "A required daily Pickle Wall training plan gives every athlete purposeful repetitions between team sessions.",
  },
  {
    icon: BarChart3,
    title: "Measured Progress",
    copy: "DUPR development, skill benchmarks, coach evaluations, and clear goals make improvement visible and accountable.",
  },
  {
    icon: Trophy,
    title: "Competition Pathway",
    copy: "Tournament preparation, team competition, and planned travel opportunities help athletes learn how to perform under pressure.",
  },
];

const included = [
  [Award, "Pro-style coaching and player development"],
  [Sparkles, "Weekly guest experts and specialty training"],
  [Dumbbell, "Strength, conditioning, mobility, and recovery guidance"],
  [BarChart3, "DUPR tracking and individual performance goals"],
  [Trophy, "Tournament preparation and team competition"],
  [CalendarDays, "Planned tournament and travel calendar"],
  [ShieldCheck, "Official team uniforms"],
  [Backpack, "Pickle Wall Academy team bag"],
];

const weeklyRhythm = [
  {
    number: "01",
    title: "Two team sessions",
    copy: "Live coaching, partner work, match play, footwork, and tactical development twice each week.",
  },
  {
    number: "02",
    title: "Daily home training",
    copy: "Short, structured assignments using the Pickle Wall system to build touch, control, consistency, and confidence.",
  },
  {
    number: "03",
    title: "Expert development",
    copy: "Weekly specialty instruction may include performance, mindset, nutrition, recovery, movement, and high-level pickleball strategy.",
  },
  {
    number: "04",
    title: "Progress review",
    copy: "Coaches evaluate development, update goals, and help each athlete prepare for DUPR-rated play and tournament competition.",
  },
];

const experienceLevels = [
  "New to pickleball",
  "Recreational player",
  "Competitive / tournament player",
  "Other sport background, new to pickleball",
];

const shirtSizes = [
  "Youth S",
  "Youth M",
  "Youth L",
  "Youth XL",
  "Adult S",
  "Adult M",
  "Adult L",
  "Adult XL",
  "Adult 2XL",
  "Adult 3XL",
];

const STRIPE_TRYOUT_CHECKOUT_URL = "https://book.stripe.com/bJe14magVfMBaLleQL9MY00";

const initialForm = {
  guardianName: "",
  email: "",
  phone: "",
  athleteName: "",
  athleteAge: "",
  experienceLevel: "",
  currentDupr: "",
  background: "",
  shirtSize: "",
  consent: false,
};

export default function AcademyPage() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const formStarted = useRef(false);

  useEffect(() => {
    document.title = "Pickle Wall Academy | Central Florida Youth Pickleball Development";
    trackEvent("academy_page_view", { page_path: "/academy" });
    const description =
      "Pickle Wall Academy is a pro-style Central Florida development program for athletes ages 10–18, with twice-weekly team coaching, daily training, DUPR development, tournaments, and travel opportunities.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, []);

  const update = (key) => (event) => {
    if (!formStarted.current) {
      formStarted.current = true;
      trackEvent("academy_form_started", { form_name: "academy-interest-form" });
    }
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (
      !form.guardianName ||
      !form.email ||
      !form.phone ||
      !form.athleteName ||
      !form.athleteAge ||
      !form.experienceLevel ||
      !form.shirtSize
    ) {
      toast.error("Please complete all required registration fields.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm parent/guardian consent to continue.");
      return;
    }

    setLoading(true);
    try {
      const message = [
        `Athlete: ${form.athleteName}`,
        `Athlete age: ${form.athleteAge}`,
        `Experience: ${form.experienceLevel}`,
        `Current DUPR: ${form.currentDupr || "Not provided"}`,
        `Sports background: ${form.background || "Not provided"}`,
        `T-shirt size: ${form.shirtSize}`,
        `Parent/guardian consent: Confirmed`,
      ].join("\n");

      await api.post("/leads", {
        name: form.guardianName,
        email: form.email,
        phone: form.phone,
        source: "pickle-wall-academy",
        message,
        attribution: attributionForSubmit(),
      });
      trackEvent("academy_form_submitted", { academy_program: "team-tryouts" });
      toast.success("Registration saved — redirecting to secure payment...");
      const checkoutUrl = new URL(STRIPE_TRYOUT_CHECKOUT_URL);
      checkoutUrl.searchParams.set("prefilled_email", form.email);
      trackEvent("academy_tryout_checkout_started", { value: 59, currency: "USD", academy_program: "team-tryouts" });
      window.setTimeout(() => {
        window.location.href = checkoutUrl.toString();
      }, 900);
    } catch (error) {
      toast.error("We couldn’t save your registration. Please try again.");
      setLoading(false);
    }
  };

  return (
    <main id="main-content" className="min-h-screen bg-[#070707] text-white">
      <Navbar />

      <section className="relative overflow-hidden pt-28 md:pt-36 border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_25%,rgba(255,107,0,0.18),transparent_32%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-20">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 border border-[#FF6B00]/40 bg-[#FF6B00]/10 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#FF6B00]">
                <MapPin size={14} /> Central Florida · Ages 10–18
              </div>
              <h1 className="mt-7 font-display text-6xl sm:text-7xl lg:text-[104px] font-black uppercase tracking-tight leading-[0.82]">
                Build the
                <span className="block text-[#FF6B00]">complete player.</span>
              </h1>
              <p className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-white/70">
                Pickle Wall Academy combines live team coaching, structured daily training,
                measurable DUPR development, and a real pathway into tournament competition.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <a
                  href="#tryouts"
                  data-testid="academy-hero-cta-tryouts"
                  onClick={() => trackEvent("academy_apply_click", { button_location: "academy-hero" })}
                  className="inline-flex items-center justify-center gap-2 bg-[#FF6B00] px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-black hover:bg-[#ff8129] transition-colors"
                >
                  Register &amp; Pay $59 <ArrowRight size={18} />
                </a>
                <a
                  href="#program"
                  data-testid="academy-hero-cta-program"
                  className="inline-flex items-center justify-center border border-white/20 px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors"
                >
                  See the program
                </a>
              </div>

              <div className="mt-10 grid grid-cols-3 max-w-xl border-y border-white/10 py-5">
                <Stat value="2×" label="Weekly team sessions" />
                <Stat value="Daily" label="Training system" border />
                <Stat value="$59" label="Tryout registration" />
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-[540px]">
              <div className="absolute -inset-5 bg-[#FF6B00]/20 blur-3xl" />
              <img
                src="/pickle-wall-academy-tryouts.png"
                alt="Pickle Wall Academy Central Florida team tryouts for athletes ages 10 to 18"
                className="relative w-full border border-white/10 shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="program" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="The development model"
            title={<>More than lessons.<br /><span className="text-[#FF6B00]">A complete system.</span></>}
            copy="The Academy connects what happens with a coach to what an athlete does every day. Every repetition has a purpose, and every player has a development path."
          />
          <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            {pillars.map((pillar) => (
              <article key={pillar.title} className="group border border-white/10 bg-[#0d0d0d] p-7 hover:border-[#FF6B00]/50 transition-colors">
                <div className="grid h-12 w-12 place-items-center bg-[#FF6B00]/10 text-[#FF6B00]">
                  <pillar.icon size={24} />
                </div>
                <h3 className="mt-7 font-display text-3xl font-black uppercase">{pillar.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/60">{pillar.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0b0b0b] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[0.8fr_1.2fr] gap-14 lg:gap-24">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.28em] text-[#FF6B00]">The weekly rhythm</div>
            <h2 className="mt-5 font-display text-5xl md:text-7xl font-black uppercase leading-[0.9]">
              Coaching twice.<br />Training daily.
            </h2>
            <p className="mt-6 max-w-md text-white/60 leading-relaxed">
              Team practices create direction. Daily assignments create the repetitions required to turn instruction into instinct.
            </p>
          </div>
          <div className="space-y-3">
            {weeklyRhythm.map((item) => (
              <div key={item.number} className="grid grid-cols-[54px_1fr] gap-5 border border-white/10 bg-black/20 p-6">
                <div className="font-display text-3xl font-black text-[#FF6B00]">{item.number}</div>
                <div>
                  <h3 className="font-display text-2xl font-black uppercase">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{item.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="The Academy experience"
            title={<>Train like a team.<br /><span className="text-[#FF6B00]">Compete like one.</span></>}
            copy="Selected athletes enter a structured environment built around preparation, accountability, team identity, and meaningful competition."
          />

          <div className="mt-14 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {included.map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-4 bg-[#080808] p-5 md:p-6">
                <Icon size={20} className="shrink-0 text-[#FF6B00]" />
                <span className="font-bold text-white/80">{label}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 border-l-4 border-[#FF6B00] bg-[#FF6B00]/10 p-6 text-sm leading-relaxed text-white/60">
            Exact practice locations, tournament entries, travel dates, and final team benefits will be shared directly with registered families. Tournament participation and travel may involve additional costs depending on the event.
          </div>
        </div>
      </section>

      <section id="tryouts" className="relative overflow-hidden border-t border-white/10 bg-[#0b0b0b] py-24 md:py-32">
        <div className="absolute right-0 top-0 h-[520px] w-[520px] rounded-full bg-[#FF6B00]/10 blur-[150px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.28em] text-[#FF6B00]">Central Florida team tryouts</div>
            <h2 className="mt-5 font-display text-6xl md:text-8xl font-black uppercase leading-[0.86]">
              Your next level<br /><span className="text-[#FF6B00]">starts here.</span>
            </h2>
            <p className="mt-7 text-lg leading-relaxed text-white/60">
              Register your athlete and pay the $59 tryout fee to reserve an evaluation slot. The $59 tryout fee reserves one athlete evaluation and founding-team consideration. Tryout registration does not guarantee team placement.
            </p>
            <div className="mt-8 space-y-4">
              {["Athletes ages 10–18", "All current skill levels considered", "Competitive mindset and commitment required"].map((item) => (
                <div key={item} className="flex items-center gap-3 text-sm text-white/80">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-[#FF6B00] text-black"><Check size={14} strokeWidth={3} /></span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={submit} noValidate className="border border-white/10 bg-[#080808] p-7 md:p-10" data-testid="academy-interest-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Parent / Guardian Name" value={form.guardianName} onChange={update("guardianName")} required data-testid="academy-guardian-name" />
              <Field label="Parent / Guardian Email" type="email" value={form.email} onChange={update("email")} required data-testid="academy-guardian-email" />
              <Field label="Phone Number" type="tel" value={form.phone} onChange={update("phone")} required data-testid="academy-phone" />
              <Field label="Athlete Full Name" value={form.athleteName} onChange={update("athleteName")} required data-testid="academy-athlete-name" />
              <label className="block">
                <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-white/50">Athlete Age *</span>
                <select
                  value={form.athleteAge}
                  onChange={update("athleteAge")}
                  required
                  data-testid="academy-athlete-age"
                  className="w-full rounded-none border border-white/10 bg-[#111] px-4 py-4 text-white outline-none focus:border-[#FF6B00]"
                >
                  <option value="">Select age (10–18)</option>
                  {Array.from({ length: 9 }, (_, i) => i + 10).map((age) => (
                    <option key={age} value={age}>{age}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-white/50">Current Pickleball / Athletic Experience *</span>
                <select
                  value={form.experienceLevel}
                  onChange={update("experienceLevel")}
                  required
                  data-testid="academy-experience"
                  className="w-full rounded-none border border-white/10 bg-[#111] px-4 py-4 text-white outline-none focus:border-[#FF6B00]"
                >
                  <option value="">Select experience</option>
                  {experienceLevels.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </label>
              <Field label="Current DUPR (optional)" value={form.currentDupr} onChange={update("currentDupr")} placeholder="New players welcome" data-testid="academy-current-dupr" />
              <label className="block">
                <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-white/50">Academy T-Shirt Size *</span>
                <select
                  value={form.shirtSize}
                  onChange={update("shirtSize")}
                  required
                  data-testid="academy-shirt-size"
                  className="w-full rounded-none border border-white/10 bg-[#111] px-4 py-4 text-white outline-none focus:border-[#FF6B00]"
                >
                  <option value="">Select T-shirt size</option>
                  {shirtSizes.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="mt-4 block">
              <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-white/50">Athlete sports background (optional)</span>
              <textarea
                value={form.background}
                onChange={update("background")}
                rows={4}
                placeholder="Pickleball, volleyball, tennis, or other athletic experience"
                data-testid="academy-background"
                className="w-full resize-none rounded-none border border-white/10 bg-[#111] px-4 py-4 text-white outline-none placeholder:text-white/25 focus:border-[#FF6B00]"
              />
            </label>
            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={update("consent")}
                required
                data-testid="academy-consent"
                className="mt-1 h-5 w-5 shrink-0 accent-[#FF6B00] cursor-pointer"
              />
              <span className="text-sm leading-relaxed text-white/70">
                I am this athlete's parent/guardian and I consent to registration. I understand the $59 tryout fee reserves one athlete evaluation and founding-team consideration, and that tryout registration does not guarantee team placement. *
              </span>
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="academy-submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-[#FF6B00] px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-black hover:bg-[#ff8129] disabled:opacity-60 transition-colors"
            >
              {loading ? "Saving…" : "Register & Pay $59"} <ArrowRight size={18} />
            </button>
            <p className="mt-4 text-center text-xs leading-relaxed text-white/40">
              The $59 tryout fee reserves one athlete evaluation and founding-team consideration. Tryout registration does not guarantee team placement. A parent or guardian must complete registration for athletes under 18.
            </p>
          </form>
        </div>
      </section>

      <section className="bg-[#FF6B00] py-10 text-black">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <div className="font-display text-3xl md:text-4xl font-black uppercase">Hold the line. Build the future.</div>
            <div className="mt-1 text-sm font-semibold text-black/65">Pickle Wall Academy · Central Florida</div>
          </div>
          <Link to="/" data-testid="academy-explore-pickle-wall" className="text-sm font-black uppercase tracking-[0.14em] underline underline-offset-4">Explore Pickle Wall</Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function Stat({ value, label, border = false }) {
  return (
    <div className={`px-3 text-center ${border ? "border-x border-white/10" : ""}`}>
      <div className="font-display text-2xl md:text-3xl font-black text-[#FF6B00]">{value}</div>
      <div className="mt-1 text-[9px] md:text-[10px] uppercase tracking-wider text-white/50">{label}</div>
    </div>
  );
}

function SectionHeading({ eyebrow, title, copy }) {
  return (
    <div className="grid lg:grid-cols-[1fr_0.7fr] gap-8 lg:items-end">
      <div>
        <div className="text-xs font-black uppercase tracking-[0.28em] text-[#FF6B00]">{eyebrow}</div>
        <h2 className="mt-5 font-display text-5xl md:text-7xl font-black uppercase leading-[0.9]">{title}</h2>
      </div>
      <p className="max-w-xl text-base md:text-lg leading-relaxed text-white/60">{copy}</p>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-white/50">{label}{props.required ? " *" : ""}</span>
      <input
        {...props}
        className="w-full rounded-none border border-white/10 bg-[#111] px-4 py-4 text-white outline-none placeholder:text-white/25 focus:border-[#FF6B00]"
      />
    </label>
  );
}
